import { PortfolioItem, PersonalInfo } from "@/types/portfolio";
import { SiteConfig } from "@/types/siteconfig";
import { supabase } from "@/integrations/supabase/client";
import { logError } from "@/lib/logger";

export const storage = {
  async getPersonalInfo(): Promise<PersonalInfo | null> {
    try {
      console.log('[storage.getPersonalInfo] Starting query...');
      // Use the public view for non-admin access
      const { data, error } = await supabase
        .from('public_personal_info')
        .select('*')
        .limit(1)
        .maybeSingle();
      
      console.log('[storage.getPersonalInfo] Query result:', { data, error });

      if (error) {
        console.error('[storage.getPersonalInfo] Error:', error);
        throw error;
      }
      
      if (!data) {
        console.warn('[storage.getPersonalInfo] No data returned');
        return null;
      }

      const transformedData = {
        first_name: data.first_name,
        last_name: data.last_name || '',
        oneliner: data.oneliner || '',
        bio: data.bio || '',
        avatar: data.avatar,
        email: data.email || '',
        emails: data.emails as any || [],
        phone: data.phone || '',
        office_phone: data.office_phone,
        phones: data.phones as any || [],
        location: data.location || '',
        address: data.address,
        website: data.website,
        socials: data.socials as any || {},
        social_order: data.social_order as string[] || ["linkedin","twitter","github","instagram","youtube","newsletter"],
        social_config: data.social_config as any,
        appointment: data.appointment,
        field_visibility: data.field_visibility as any,
      };

      console.log('[storage.getPersonalInfo] Transformed data:', transformedData);
      return transformedData;
    } catch (error) {
      console.error('[storage.getPersonalInfo] Catch block:', error);
      logError(error, 'storage.getPersonalInfo');
      return null;
    }
  },

  async setPersonalInfo(info: PersonalInfo): Promise<void> {
    try {
      // Get existing record
      const { data: existing } = await supabase
        .from('personal_info')
        .select('id')
        .limit(1)
        .single();

      if (existing) {
        // Update existing
        const { error } = await supabase
          .from('personal_info')
          .update({
            first_name: info.first_name,
            last_name: info.last_name,
            oneliner: info.oneliner,
            bio: info.bio,
            avatar: info.avatar,
            email: info.email,
            emails: info.emails as any,
            phone: info.phone,
            office_phone: info.office_phone,
            phones: info.phones as any,
            location: info.location,
            address: info.address,
            website: info.website,
            socials: info.socials,
            social_order: info.social_order,
            social_config: info.social_config as any,
            appointment: info.appointment,
            field_visibility: info.field_visibility as any,
          })
          .eq('id', existing.id);
        
        if (error) throw error;
      } else {
        // Insert new
        const { error } = await supabase
          .from('personal_info')
          .insert({
            first_name: info.first_name,
            last_name: info.last_name,
            oneliner: info.oneliner,
            bio: info.bio,
            avatar: info.avatar,
            email: info.email,
            emails: info.emails as any,
            phone: info.phone,
            office_phone: info.office_phone,
            phones: info.phones as any,
            location: info.location,
            address: info.address,
            website: info.website,
            socials: info.socials,
            social_order: info.social_order,
            social_config: info.social_config as any,
            appointment: info.appointment,
            field_visibility: info.field_visibility as any,
          });
        
        if (error) throw error;
      }
    } catch (error) {
      logError(error, 'storage.setPersonalInfo');
      throw error;
    }
  },

  async getPortfolioItems(): Promise<PortfolioItem[]> {
    try {
      const { data, error } = await supabase
        .from('portfolio_items')
        .select('*')
        .order('order', { ascending: true });
      
      if (error) throw error;
      
      return (data || []).map(item => ({
        id: item.id,
        name: item.name,
        role: item.role as any,
        status: item.status as any,
        summary: item.summary || '',
        tags: item.tags || [],
        url: item.url || '',
        logo: item.logo,
        order: item.order,
      }));
    } catch (error) {
      logError(error, 'storage.getPortfolioItems');
      return [];
    }
  },

  async setPortfolioItems(items: PortfolioItem[]): Promise<void> {
    try {
      // Delete all existing items
      await supabase.from('portfolio_items').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      
      // Insert new items
      const { error } = await supabase
        .from('portfolio_items')
        .insert(items.map(item => ({
          id: item.id,
          name: item.name,
          role: item.role,
          status: item.status,
          summary: item.summary,
          tags: item.tags,
          url: item.url,
          logo: item.logo,
          order: item.order,
        })));
      
      if (error) throw error;
    } catch (error) {
      logError(error, 'storage.setPortfolioItems');
      throw error;
    }
  },

  async addPortfolioItem(item: Omit<PortfolioItem, 'id'>): Promise<PortfolioItem> {
    try {
      const { data, error } = await supabase
        .from('portfolio_items')
        .insert({
          name: item.name,
          role: item.role,
          status: item.status,
          summary: item.summary,
          tags: item.tags,
          url: item.url,
          logo: item.logo,
          order: item.order,
        })
        .select()
        .single();
      
      if (error) throw error;
      
      return {
        id: data.id,
        name: data.name,
        role: data.role as any,
        status: data.status as any,
        summary: data.summary || '',
        tags: data.tags || [],
        url: data.url || '',
        logo: data.logo,
        order: data.order,
      };
    } catch (error) {
      logError(error, 'storage.addPortfolioItem');
      throw error;
    }
  },

  async updatePortfolioItem(id: string, updates: Partial<PortfolioItem>): Promise<void> {
    try {
      const { error } = await supabase
        .from('portfolio_items')
        .update(updates)
        .eq('id', id);
      
      if (error) throw error;
    } catch (error) {
      logError(error, 'storage.updatePortfolioItem');
      throw error;
    }
  },

  async deletePortfolioItem(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('portfolio_items')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    } catch (error) {
      logError(error, 'storage.deletePortfolioItem');
      throw error;
    }
  },

  async getThemeConfig() {
    try {
      const { data, error } = await supabase
        .from('theme_config')
        .select('*')
        .single();
      
      if (error && error.code !== 'PGRST116') throw error;
      return data;
    } catch (error) {
      logError(error, 'storage.getThemeConfig');
      return null;
    }
  },

  async setThemeConfig(config: any) {
    try {
      const { data: existing } = await supabase
        .from('theme_config')
        .select('id')
        .single();

      if (existing) {
        const { error } = await supabase
          .from('theme_config')
          .update(config)
          .eq('id', existing.id);
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('theme_config')
          .insert(config);
        
        if (error) throw error;
      }
    } catch (error) {
      logError(error, 'storage.setThemeConfig');
      throw error;
    }
  },

  async getSiteConfig(): Promise<SiteConfig | null> {
    try {
      const { data, error } = await supabase
        .from('site_config')
        .select('*')
        .limit(1)
        .maybeSingle();
      
      if (error && error.code !== 'PGRST116') throw error;
      return data;
    } catch (error) {
      logError(error, 'storage.getSiteConfig');
      return null;
    }
  },

  async setSiteConfig(config: Partial<SiteConfig>): Promise<void> {
    try {
      const { data: existing } = await supabase
        .from('site_config')
        .select('id')
        .limit(1)
        .maybeSingle();

      if (existing) {
        const { error } = await supabase
          .from('site_config')
          .update(config)
          .eq('id', existing.id);
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('site_config')
          .insert(config);
        
        if (error) throw error;
      }
    } catch (error) {
      logError(error, 'storage.setSiteConfig');
      throw error;
    }
  },
};
